import {
  getPropertyNameForIncompleteTimeWindow,
  INCOMPLETE_END_SUFFIX,
  INCOMPLETE_START_SUFFIX,
} from '@modules/charts/components/LineChartWrapper/helpers/getPropertyNameForIncompleteTimeWindow';
import {
  LegendLabel,
  LineChartDataPoint,
  LineChartInputData,
} from '@modules/charts/types';
import { getFormattedTimeWindow } from '@modules/charts/utils';
import { SortBy } from '@modules/common/types';
import { getDateRangeEndTime, toSentimentName } from '@modules/common/utils';
import { isTimeAfterDateRange } from '@modules/common/utils/isTimeAfterDateRange';
import { isTimeBeforeDateRange } from '@modules/common/utils/isTimeBeforeDateRange';
import dayjs from 'dayjs';
import { SentimentType, TimeUnit } from 'generated/graphql';
import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { TimeRange } from 'v2/core/entities';
import { useGetUser } from 'v2/core/services/user';

interface TransformLineChartDataOption {
  data: LineChartInputData[];
  timestamps: number[];
  aggregatePeriod?: TimeUnit;
  dateRange: TimeRange;
  sortBy?: SortBy;
}

export const useTransformLineChartData = ({
  data,
  timestamps,
  aggregatePeriod,
  dateRange,
  sortBy,
}: TransformLineChartDataOption) => {
  const {
    tenant: { timezone },
  } = useGetUser();

  const [lineChartData, setLineChartData] = useState<LineChartDataPoint[]>([]);

  const getLabelDisplayName = (
    name: string,
    sentiment: SentimentType | undefined,
  ) => {
    return sentiment ? `${name} - ${toSentimentName(sentiment)}` : name;
  };

  const { groupedByDateRangeData, yMax } = useMemo(() => {
    const resultObj: Record<
      string,
      {
        [label: string]:
          | Pick<LineChartInputData, 'id' | 'value' | 'anomalyValue'>
          | number
          | boolean;
        timestamp: number;
        isAnomalous: boolean;
      }
    > = {};

    let yMax = 0;

    data.forEach((dataObj) => {
      if (!dataObj.timestamp) {
        return;
      }
      const labelDisplayName = getLabelDisplayName(
        dataObj.label,
        dataObj.entitySentiment,
      );

      if (dataObj.timestamp in resultObj) {
        resultObj[dataObj.timestamp] = {
          ...resultObj[dataObj.timestamp],
          [labelDisplayName]: {
            id: dataObj.id,
            value: dataObj.value,
            anomalyValue: dataObj.anomalyValue,
          },
          isAnomalous: !resultObj[dataObj.timestamp].isAnomalous
            ? !!dataObj.anomalyValue
            : true,
        };
      } else {
        resultObj[dataObj.timestamp] = {
          [labelDisplayName]: {
            id: dataObj.id,
            value: dataObj.value,
            anomalyValue: dataObj.anomalyValue,
          },
          timestamp: dataObj.timestamp,
          isAnomalous: !!dataObj.anomalyValue,
        };
      }

      if (dataObj.value > yMax) {
        yMax = dataObj.value;
      }
    });

    timestamps.forEach((timestamp) => {
      if (!resultObj[timestamp]) {
        resultObj[timestamp] = { timestamp, isAnomalous: false };
      }
    });

    return { groupedByDateRangeData: resultObj, yMax };
  }, [data, timestamps]);

  const labelsMap = useMemo(() => {
    const map = new Map<string, LegendLabel>();
    data.forEach((dataObj) => {
      const { id, label, entitySentiment, value, timestamp, category } =
        dataObj;

      if (!id || !label) {
        return;
      }
      const labelToUse = entitySentiment
        ? getLabelDisplayName(label, entitySentiment)
        : label;
      const existingLabel = map.get(labelToUse);
      const valueToSortBy =
        (existingLabel?.value ?? 0) +
        (timestamp?.toString() === sortBy?.field.toString() ||
        sortBy?.field === 'value'
          ? value
          : 0);
      map.set(labelToUse, {
        id: entitySentiment || id,
        name: labelToUse,
        value: valueToSortBy,
        category,
      });
    });
    const sortedLabels = orderBy(
      [...map],
      [(entry) => entry[1]?.value ?? 0],
      [sortBy?.isAsc ? 'asc' : 'desc'],
    );
    return new Map(sortedLabels);
  }, [data, sortBy]);

  const labelsList = Array.from(labelsMap.values());

  const mappedLineChartData = useMemo((): LineChartDataPoint[] => {
    const sortedLineChartData = Object.values(groupedByDateRangeData).sort(
      (a, b) => a.timestamp - b.timestamp,
    );

    const firstTimeWindow =
      sortedLineChartData.length > 0
        ? dayjs(sortedLineChartData[0].timestamp).tz()
        : undefined;
    const lastTimeWindow =
      sortedLineChartData.length > 0
        ? dayjs(
            sortedLineChartData[sortedLineChartData.length - 1].timestamp,
          ).tz()
        : undefined;

    const populatedLineChartData = sortedLineChartData.map(
      (dataPoint, index, dataPoints) => {
        const startDate = dayjs(dataPoint.timestamp).tz();
        const endDate = getDateRangeEndTime(startDate, aggregatePeriod);

        const resultObj: LineChartDataPoint = {
          ...dataPoint,
          isIncompletePeriod: false,
          dateRange: getFormattedTimeWindow({
            startDate,
            endDate,
            aggregatePeriod,
            firstTimeWindow,
            lastTimeWindow,
          }),
        };

        labelsList.forEach((label) => {
          if (!resultObj[label.name]) {
            resultObj[label.name] = {
              id: label.id,
              value: 0,
              anomalyValue: undefined,
            };
          }
        });

        // incomplete window is always at first or last time-window
        if (index === 0 || index === dataPoints.length - 1) {
          resultObj.isIncompletePeriod =
            isTimeBeforeDateRange(startDate, dateRange, timezone) ||
            isTimeAfterDateRange(endDate, dateRange, timezone);
        }

        return resultObj;
      },
    );

    if (populatedLineChartData.length <= 1) {
      return populatedLineChartData;
    }

    const isInadequateDataPointsSize = populatedLineChartData.length <= 3;

    return populatedLineChartData.map((dataPoint, index, dataPoints) => {
      const resultObj: LineChartDataPoint = { ...dataPoint };

      const isFirstOrAdjacentIndex = index === 0 || index === 1;

      const isFirstIncompleteDataPoint =
        dataPoints[0].isIncompletePeriod && isFirstOrAdjacentIndex;

      const isLastOrAdjacentIndex =
        index === dataPoints.length - 1 || index === dataPoints.length - 2;

      const isLastIncompleteDataPoint =
        dataPoints[dataPoints.length - 1].isIncompletePeriod &&
        isLastOrAdjacentIndex;

      if (isFirstIncompleteDataPoint || isLastIncompleteDataPoint) {
        labelsList.forEach((label) => {
          resultObj[
            getPropertyNameForIncompleteTimeWindow({
              displayName: label.name,
              incompleteStartEndSuffix:
                isFirstIncompleteDataPoint || isInadequateDataPointsSize
                  ? INCOMPLETE_START_SUFFIX
                  : INCOMPLETE_END_SUFFIX,
            })
          ] = resultObj[label.name];

          if (index === 0 || index === dataPoints.length - 1) {
            delete resultObj[label.name];
          }
        });
      }
      return resultObj;
    });
  }, [
    aggregatePeriod,
    dateRange,
    groupedByDateRangeData,
    labelsList,
    timezone,
  ]);

  const hasIncompleteTimeWindow = Boolean(
    lineChartData.find((dataPoint) => dataPoint.isIncompletePeriod),
  );

  // Workaround for line dots render issue: https://github.com/recharts/recharts/issues/804
  // To prevent cancellation of initial animation cycle, set data state only when there are actual changes
  useDeepCompareEffect(() => {
    setLineChartData(mappedLineChartData);
  }, [mappedLineChartData]);

  return {
    lineChartData,
    labelsList,
    labelsMap,
    hasIncompleteTimeWindow,
    yMax,
  };
};
